import type { ReactNode, CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import './attention-pulse.css';

export interface AttentionPulseProps {
  /** Si es false, no renderiza ninguna animación ni marcado extra — pasa el children tal cual */
  active: boolean;
  /** Radio de borde del elemento envuelto. Debe coincidir con el radius real del botón/tarjeta. Default: 8 (Button size="md") */
  radius?: number;
  /** Qué tan grande crece la onda, en px. Default: 8 */
  size?: number;
  /** Duración de un ciclo completo (crecida + pausa), en segundos. Default: 2.7 */
  duration?: number;
  /** Espera antes de mostrar el halo, para no aparecer en el frame 0 junto con el resto de la pantalla. Default: 1500ms */
  entranceDelayMs?: number;
  children: ReactNode;
}

/**
 * Envuelve cualquier elemento (botón, ícono, card) con un efecto de doble eco
 * pensado para señalar "esto es nuevo" sin ser invasivo. Se apaga solo: pasar
 * un `active` que llegue a false (ej. vía useFirstTimeAttention o useTimeBoxedDailyAttention).
 *
 * No modifica el elemento hijo — es un wrapper puramente visual.
 */
export function AttentionPulse({
  active,
  radius = 8,
  size = 8,
  duration = 2.7,
  entranceDelayMs = 1500,
  children,
}: AttentionPulseProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!active) {
      setVisible(false);
      return;
    }
    const timer = setTimeout(() => setVisible(true), entranceDelayMs);
    return () => clearTimeout(timer);
  }, [active, entranceDelayMs]);

  const style = {
    '--pulse-radius': `${radius}px`,
    '--pulse-size': `${size}px`,
    '--pulse-duration': `${duration}s`,
    '--pulse-delay': `${duration * 0.55}s`, // ~mismo ratio que el diseño validado (1.49s de 2.7s)
  } as CSSProperties;

  return (
    <span
      className={visible ? 'attention-pulse-wrap' : undefined}
      style={visible ? style : undefined}
    >
      {children}
    </span>
  );
}
